import { createPalette, PaletteAtomButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Badge, type SidebarButtonProps, Tooltip, useSidebarItem } from '@gitbook/spine-react';

import { DotIndicator } from '../../Screens/Space/ChangeRequest/DotIndicator';
import { useContactSupport } from '../../state';
import { useIntercomUnreadCount } from '../../state/support-intercom';
import { createDocumentationURL } from '../../utils/createDocumentationURL';

/**
 * A palette button giving access to various actions related to getting help.
 */
export function HelpButton(props: Pick<SidebarButtonProps, 'iconOnly' | 'outlined' | 'label'>) {
    const { iconOnly = true, outlined = true, label = 'Help' } = props;

    const contactSupport = useContactSupport();
    const unreadCount = useIntercomUnreadCount();
    const hasUnread = unreadCount > 0;

    const { itemProps } = useSidebarItem({
        iconOnly,
        outlined,
        label: hasUnread ? `${label} (${unreadCount} new)` : label,
        icon: Icon.HelpCircle,
        className: 'relative',
        elementType: 'button',
        children:
            hasUnread &&
            (iconOnly ? (
                <DotIndicator kind="primary" className="absolute top-0 right-0" />
            ) : (
                <Badge
                    label={unreadCount}
                    kind="secondary"
                    size="xsmall"
                    className="ml-auto"
                    thin
                />
            )),
    });

    return (
        <Tooltip label={label} disabled={!iconOnly}>
            <PaletteAtomButton
                palette={() =>
                    createPalette({
                        groups: [
                            {
                                key: 'help',
                                commands: [
                                    {
                                        key: 'help.keyboard_shortcuts',
                                        title: 'Keyboard shortcuts',
                                        icon: Icon.EnterKey,
                                        trigger: () => {
                                            window.open(
                                                createDocumentationURL(
                                                    'resources/keyboard-shortcuts'
                                                ),
                                                '_blank'
                                            );
                                        },
                                    },
                                    {
                                        key: 'help.changelog',
                                        title: 'Changelog',
                                        icon: Icon.DeviceDesktop,
                                        trigger: () => {
                                            window.open(
                                                createDocumentationURL('changelog'),
                                                '_blank'
                                            );
                                        },
                                    },
                                    {
                                        key: 'help.community',
                                        title: 'Community',
                                        icon: Icon.GitHub,
                                        trigger: () => {
                                            window.open(
                                                'https://github.com/GitbookIO/community/discussions',
                                                '_blank'
                                            );
                                        },
                                    },
                                ],
                            },
                            {
                                key: 'help_contact',
                                commands: [
                                    {
                                        key: 'help.suggest_feature',
                                        title: 'Suggest a feature',
                                        icon: Icon.Bulb,
                                        trigger: () => {
                                            window.open(
                                                'https://github.com/orgs/GitbookIO/discussions/categories/feature-requests',
                                                '_blank'
                                            );
                                        },
                                    },
                                    {
                                        key: 'help.contact_support',
                                        title: 'Contact us',
                                        icon: Icon.Send,
                                        badge: hasUnread
                                            ? {
                                                  label:
                                                      unreadCount === 1
                                                          ? '1 new message'
                                                          : `${unreadCount} new messages`,
                                                  kind: 'danger',
                                              }
                                            : undefined,
                                        trigger: async () => {
                                            contactSupport();
                                        },
                                    },
                                ],
                            },
                        ],
                    })
                }
                {...itemProps}
                className={itemProps.className}
            />
        </Tooltip>
    );
}
